import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardActionArea,
  Fade
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import request from '../api';
import { Store } from '../store.jsx';
import { CATEGORY_LIST_REQUEST, PRODUCT_LIST_REQUEST } from '../constants';
import { MATRIZID } from '../constants';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4, 2),
    minHeight: '100vh',
    backgroundColor: '#f5f7fb' // fundo mais claro para aumentar contraste com os cards
  },
  header: {
    marginBottom: theme.spacing(4),
    textAlign: 'center'
  },
  title: {
    fontSize: '2.4rem',
    fontWeight: 800,
    color: '#1f2937',
    marginBottom: theme.spacing(0.5)
  },
  subtitle: {
    fontSize: '1.05rem',
    color: '#6b7280'
  },
  listGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  gridItem: {
    width: '100%'
  },
  restaurantCard: {
    borderRadius: 16,
    boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
    marginBottom: theme.spacing(2.5),
    border: '1px solid #e5e7eb',
    transition: 'transform 220ms ease, box-shadow 220ms ease',
    backgroundColor: '#ffffff',
    position: 'relative',
    '&:hover': {
      boxShadow: '0 16px 32px rgba(17, 24, 39, 0.12)',
      transform: 'translateY(-4px)'
    },
    '&:hover $arrowBox': {
      transform: 'translateX(4px)'
    },
    '&:hover $topMedia': {
      transform: 'scale(1.02)',
      opacity: 0.95
    }
  },
  cardTop: {
    position: 'relative',
    width: '100%',
    height: 260,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: '1px solid #eef2f7',
    [theme.breakpoints.up('lg')]: {
      height: 320
    }
  },
  topMedia: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.9,
    transition: 'opacity 200ms ease, transform 300ms ease'
  },
  topTint: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 60%)',
    pointerEvents: 'none'
  },
  infoSection: {
    padding: theme.spacing(2.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  infoTextWrap: {
    minWidth: 0,
    flex: 1
  },
  restaurantCardClosed: {
    opacity: 0.75,
    '&:hover': {
      transform: 'none',
      cursor: 'not-allowed'
    }
  },
  cardContent: {
  padding: theme.spacing(3.5),
    display: 'flex',
    alignItems: 'center',
  gap: theme.spacing(3),
  minHeight: 170
  },
  imageContainer: {
  width: 100,
  height: 100,
    borderRadius: 12,
    overflow: 'hidden',
    flexShrink: 0,
  backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e5e7eb'
  },
  restaurantImage: {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  padding: 10,
  backgroundColor: '#fff'
  },
  avatarFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.4rem',
    fontWeight: 800,
    color: 'white',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
  },
  restaurantInfo: {
    flex: 1,
    minWidth: 0
  },
  restaurantName: {
  fontSize: '1.4rem',
    fontWeight: 700,
    color: '#111827',
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.3,
    wordBreak: 'break-word',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  restaurantDescription: {
    fontSize: '0.95rem',
    color: '#6b7280',
    marginBottom: theme.spacing(1)
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.3
  },
  openBadge: {
    backgroundColor: '#e7f6ec',
    color: '#198754',
    border: '1px solid #c9e8d5'
  },
  closedBadge: {
    backgroundColor: '#f2f4f7',
    color: '#6b7280',
    border: '1px solid #e5e7eb'
  },
  scheduleText: {
    fontSize: '0.85rem',
    color: '#6b7280'
  },
  arrowBox: {
  width: 48,
  height: 48,
    borderRadius: 999,
    backgroundColor: '#eef2ff',
    color: '#3730a3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  fontSize: '1.35rem',
    fontWeight: 700,
    border: '1px solid #e0e7ff',
    transition: 'transform 200ms ease'
  },
  arrowDisabled: {
    backgroundColor: '#f3f4f6',
    borderColor: '#e5e7eb',
    color: '#9ca3af'
  },
  accentBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 6,
    borderRadius: '16px 0 0 16px'
  }
}));

// util simples: HEX -> rgba com alpha
const hexToRgba = (hex, alpha = 1) => {
  try {
    const sanitized = hex.replace('#', '');
    const bigint = parseInt(sanitized.length === 3 ? sanitized.split('').map(c => c + c).join('') : sanitized, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  } catch (e) { return hex; }
};

// Função para calcular próxima abertura
const calcularProximaAbertura = (horariosFuncionamento) => {
  if (!Array.isArray(horariosFuncionamento) || horariosFuncionamento.length === 0) {
    return null;
  }

  const agora = new Date();
  const diaAtual = agora.getDay() + 1; // Converter para formato da API (1=domingo)
  const minutoAtual = agora.getHours() * 60 + agora.getMinutes();

  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const getDayName = (day) => {
    const days = {
      1: 'Domingo', 2: 'Segunda-feira', 3: 'Terça-feira', 4: 'Quarta-feira',
      5: 'Quinta-feira', 6: 'Sexta-feira', 7: 'Sábado'
    };
    return days[day] || 'Dia desconhecido';
  };

  // Primeiro, verificar se ainda abre hoje
  const horarioHoje = horariosFuncionamento.find(h => h.dia === diaAtual);
  if (horarioHoje) {
    const aberturaHoje = timeToMinutes(horarioHoje.abertura);
    if (minutoAtual < aberturaHoje) {
      return `Hoje às ${horarioHoje.abertura}`;
    }
  }

  // Buscar próximo dia com funcionamento
  for (let i = 1; i <= 7; i++) {
    const proximoDia = ((diaAtual - 1 + i) % 7) + 1;
    const horarioProximo = horariosFuncionamento.find(h => h.dia === proximoDia);
    
    if (horarioProximo) {
      const nomeDia = getDayName(proximoDia);
      if (i === 1) {
        return `Amanhã às ${horarioProximo.abertura}`;
      } else {
        return `${nomeDia} às ${horarioProximo.abertura}`;
      }
    }
  }

  return null;
};

export default function SelecionarRestaurante({ history }) {
  const classes = useStyles();
  const { dispatch } = React.useContext(Store);
  const [restaurantes, setRestaurantes] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_BASE = (request?.defaults?.baseURL || '').replace(/\/$/, '');
  const buildSrc = (v) => {
    if (!v) return '';
    if (/^https?:\/\//i.test(v)) return v;
    const encoded = encodeURI(v);
    if (encoded.startsWith('/')) return `${API_BASE}${encoded}`;
    return `${API_BASE}/${encoded}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await request.get(`/restaurante/get-all?matrizId=${MATRIZID}`);
        const restaurantesComStatus = Array.isArray(data) ? data.map(restaurante => {
          // Calcular próxima abertura se estiver fechado
          let proximaAbertura = null;
          if (!restaurante.isAberto && restaurante.horariosFuncionamento) {
            proximaAbertura = calcularProximaAbertura(restaurante.horariosFuncionamento);
          }
          
          return {
            ...restaurante,
            proximaAbertura
          };
        }) : [];
        // embaralha a lista para ordem aleatória a cada carregamento
        const shuffled = [...restaurantesComStatus];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        setRestaurantes(shuffled);
      } catch (e) {
        console.error('Erro ao buscar restaurantes da matriz', e);
        setRestaurantes([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleSelect = (rest) => {
    // Não permitir seleção se o restaurante estiver fechado
    if (!rest.isAberto) {
      return;
    }
    
    localStorage.setItem('restaurante', JSON.stringify(rest));
    // Clear category/product lists so OrderScreen reloads for the new restaurant
    dispatch({ type: CATEGORY_LIST_REQUEST });
    dispatch({ type: PRODUCT_LIST_REQUEST });
    history.replace('/choose-order-type');
  };

  if (loading) return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h2" className={classes.title}>
          Selecione o Restaurante
        </Typography>
        <Typography variant="h6" className={classes.subtitle}>
          Escolha onde deseja fazer seu pedido
        </Typography>
      </Box>
      <Box style={{ maxWidth: 1200, margin: '0 auto' }} className={classes.listGrid}>
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className={classes.gridItem}>
            <Card className={classes.restaurantCard}>
              <Box className={classes.cardTop}>
                <Skeleton variant="rect" width="100%" height="100%" />
                <div className={classes.topTint} />
              </Box>
              <Box className={classes.infoSection}>
                <Box className={classes.infoTextWrap}>
                  <Skeleton variant="text" width="70%" height={28} />
                  <Skeleton variant="text" width="50%" height={20} />
                </Box>
                <Box className={classes.arrowBox}>→</Box>
              </Box>
            </Card>
          </div>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h2" className={classes.title}>
          Selecione o Restaurante
        </Typography>
        <Typography variant="h6" className={classes.subtitle}>
          Escolha onde deseja fazer seu pedido
        </Typography>
      </Box>

      <Box style={{ maxWidth: 1200, margin: '0 auto' }} className={classes.listGrid}>
  {restaurantes.map(restaurante => {
          const key = restaurante.id || restaurante.ID || restaurante.Id || restaurante.link || Math.random();
          const isOpen = restaurante.isAberto;
          const nome = restaurante.nome || restaurante.Nome;
          // descrição não exibida neste layout vertical
          const imagem = restaurante.logo || restaurante.imagem;
          const primaryColor = restaurante.corPrimaria || '#fa5b6d';

          return (
            <Fade in timeout={300} key={key}>
              <Card 
                className={`${classes.restaurantCard} ${!isOpen ? classes.restaurantCardClosed : ''}`}
                onClick={() => handleSelect(restaurante)}
                style={{ 
                  cursor: isOpen ? 'pointer' : 'not-allowed',
                  borderLeft: '0'
                }}
              >
                <CardActionArea disabled={!isOpen}>
                  {/* Topo com logo/imagem da marca */}
                  <Box className={classes.cardTop}>
                    {imagem ? (
                      <img
                        loading="lazy"
                        src={buildSrc(imagem)}
                        alt={nome}
                        className={classes.topMedia}
                        style={{ filter: `drop-shadow(0 2px 6px ${hexToRgba(primaryColor, 0.35)})` }}
                      />
                    ) : (
                      <Box className={classes.avatarFallback} style={{ width: '60%', height: '70%', borderRadius: 12 }}>
                        {nome ? nome.charAt(0).toUpperCase() : 'R'}
                      </Box>
                    )}
                    <div className={classes.topTint} />
                  </Box>

                  {/* Informações */}
                  <Box className={classes.infoSection}>
                    <Box className={classes.infoTextWrap}>
                      <Typography variant="h5" className={classes.restaurantName}>
                        {nome}
                      </Typography>
                      <Box className={classes.statusContainer}>
                        <Box className={`${classes.statusBadge} ${isOpen ? classes.openBadge : classes.closedBadge}`}>
                          {isOpen ? 'Aberto' : 'Fechado'}
                        </Box>
                        <Typography variant="body2" className={classes.scheduleText}>
                          {isOpen ? 'Atendendo agora' : (restaurante.proximaAbertura ? `Reabre ${restaurante.proximaAbertura}` : 'Horário indisponível')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Seta */}
                    <Box className={`${classes.arrowBox} ${!isOpen ? classes.arrowDisabled : ''}`}>→</Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Fade>
          );
        })}
      </Box>
    </Box>
  );
}
